import React from 'react';
import { Typography, Button } from '@mui/material';

import { DashboardT } from 'business/definitions';
import {
    DashboardSendEmailStepEnum,
    EmailRecurrence,
    ScheduledDashboardEmail,
} from '../types';
import { shouldSendEmailRightAway } from '../helpers';
import ScheduledDashboardEmailForm from '../ScheduledDashboardEmailForm/ScheduledDashboardEmailForm';
import MessagePreview from '../MessagePreview/MessagePreview';
import { useTranslation } from 'react-i18next';
import {
    useAddScheduleDashboardEmailConfigMutation,
    useEditScheduleDashboardEmailConfigMutation,
} from '../hooks/scheduleDashboards';
import { sendScheduledDashboardEmailNow } from 'helpers/dashboardHelpers';
import { trackEvent } from 'helpers/trackerHelpers';

type Props = {
    dashboard: DashboardT;
    userEmail: string;
    userId: number;
    step: DashboardSendEmailStepEnum;
    setStep: (step: DashboardSendEmailStepEnum) => void;
    email: ScheduledDashboardEmail;
    setEmail: (email: ScheduledDashboardEmail) => void;
    reset: () => void;
};

const DashboardSendEmailSteps = ({
    dashboard,
    step,
    setStep,
    email,
    setEmail,
    reset,
}: Props) => {
    const { t } = useTranslation();
    const sendNow = shouldSendEmailRightAway(email);
    const scheduledLater = email.recurrence !== EmailRecurrence.ONCE;

    const editMutation = useEditScheduleDashboardEmailConfigMutation(
        dashboard.key,
    );
    const addMutation = useAddScheduleDashboardEmailConfigMutation(
        dashboard.key,
    );
    const onEditScheduleConfig = () => {
        if (sendNow) {
            sendScheduledDashboardEmailNow(dashboard.key, email);
        }
        editMutation.mutate(email);
    };

    const handleSubmitForm = () => {
        if (email.id) {
            onEditScheduleConfig();
            setStep(DashboardSendEmailStepEnum.CONFIRMATION);
        } else {
            setStep(DashboardSendEmailStepEnum.PREVIEW);
        }
    };

    const onSubmitScheduleConfig = () => {
        if (sendNow) {
            sendScheduledDashboardEmailNow(dashboard.key, email);
        }
        addMutation.mutate(email);
        trackEvent(
            'decision/dashboard/options/send/nouvel-envoi/validé-click',
            {
                dashboard: dashboard.key,
                recurrence: email.recurrence,
                filterOption: email.filterOption,
            },
        );
    };

    const handleConfirm = () => {
        onSubmitScheduleConfig();
        setStep(DashboardSendEmailStepEnum.CONFIRMATION);
    };

    const newDashboardEmailSendButton = (
        <Button
            sx={{ width: 'fit-content' }}
            variant="contained"
            onClick={() => {
                reset();
                trackEvent(
                    'decision/dashboard/options/send/nouvel-envoi-click',
                    {
                        dashboard: dashboard.key,
                    },
                );
            }}
        >
            {t('sendDashboard.buttonNewShip')}
        </Button>
    );

    switch (step) {
        case DashboardSendEmailStepEnum.CONFIG_FORM:
            return (
                <ScheduledDashboardEmailForm
                    dashboard={dashboard}
                    scheduledEmail={email}
                    setScheduledEmail={setEmail}
                    onSubmit={handleSubmitForm}
                    onGoBack={() => setStep(DashboardSendEmailStepEnum.IDLE)}
                />
            );
        case DashboardSendEmailStepEnum.PREVIEW:
            return (
                <MessagePreview
                    email={email}
                    sendMailNow={shouldSendEmailRightAway(email)}
                    scheduledInTheFuture={
                        email.recurrence !== EmailRecurrence.ONCE
                    }
                    onGoBack={() =>
                        setStep(DashboardSendEmailStepEnum.CONFIG_FORM)
                    }
                    onSubmit={handleConfirm}
                />
            );
        case DashboardSendEmailStepEnum.CONFIRMATION:
            return (
                <>
                    {newDashboardEmailSendButton}
                    <Typography>
                        {sendNow && !scheduledLater
                            ? t('sendDashboard.emailSent')
                            : t('sendDashboard.emailSchedulingSent')}
                    </Typography>
                </>
            );

        case DashboardSendEmailStepEnum.IDLE:
        default:
            return newDashboardEmailSendButton;
    }
};

export default DashboardSendEmailSteps;
